import React, {useEffect} from 'react'
import {
    questionnaireError, questionnaireLabel, questionnaireLabelDescription,
} from 'components/Embed/styles/questionnaire'

import ReactMarkdown from 'react-markdown'
import {markdownProps} from 'components/Embed/helpers/markdown'

import {styled} from 'stitches/config'
import {
    styledTextarea,
} from 'components/Form/styles/FormView'
import {textareaWrapper} from './styles'

const StyledTextareaWrapper = styled('div', textareaWrapper)
const StyledTextarea = styled('textarea', {
    ...styledTextarea,
    boxSizing: 'border-box',
    padding: '$input',
    variants: {
        ...styledTextarea.variants,
        resizable: {
            false: {resize: 'none'},
            true: {resize: 'vertical'},
        },
    },
    width: '100%',
})
const StyledLabel = styled('label', questionnaireLabel)
const StyledLabelDescription = styled('span', questionnaireLabelDescription)
const StyledError = styled('div', questionnaireError)

export interface QuestionnaireTextAreaProps {
    id: string
    name: string
    readableName?: string
    groupName?: string
    label?: string
    labelDescription?: string
    placeholder?: string
    value: string
    rows?: number
    padding: string
    resizable?: boolean
    invalid?: boolean
    invalidText?: string
    showCorrectState?: boolean
    required?: boolean
    forceValidation?: boolean
    onChange: (params: {name: string, value: string, readableName?: string, groupName?: string}) => void
    setInvalidFields: (name: string, invalid: boolean) => void
}

export const QuestionnaireTextarea = (props: QuestionnaireTextAreaProps) => {
    const {
        placeholder, id, label, onChange, setInvalidFields,
        value, resizable, rows, forceValidation, required,
        labelDescription, name, readableName, groupName, padding,
        invalid, invalidText, showCorrectState, ...other
    } = props

    const [touched, setTouched] = React.useState(false)

    // eslint-disable-next-line no-extra-parens
    const isInvalid = (invalid && (touched || forceValidation || value)) || (!invalid && required && value === '' && (touched || forceValidation))
    const isCorrect = showCorrectState && value ? !isInvalid : false

    useEffect(() => {
        if (forceValidation && required) {
            setInvalidFields(name, !!isInvalid)
        }
    }, [forceValidation, isInvalid, required])


    return (
        <StyledTextareaWrapper>
            <StyledLabel htmlFor={id}>{label} <StyledLabelDescription>{labelDescription}</StyledLabelDescription></StyledLabel>
            <StyledTextarea
                aria-describedby={isInvalid ? `${id}-error` : undefined}
                aria-invalid={isInvalid ? 'true' : 'false'}
                correct={isCorrect}
                css={{padding}}
                id={id}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setTouched(true)
                    onChange({
                        groupName,
                        name,
                        readableName,
                        value: event.target.value,
                    })
                }}
                placeholder={placeholder}
                resizable={resizable}
                rows={rows}
                value={value}
                {...other}
            />
            {isInvalid ?
                <StyledError id={`${id}-error`} role='alert'>
                    <ReactMarkdown {...markdownProps}>
                        {invalidText || ''}
                    </ReactMarkdown>
                </StyledError>
                : null}
        </StyledTextareaWrapper>
    )
}

QuestionnaireTextarea.defaultProps = {
    forceValidation: false,
    groupName: '',
    invalid: false,
    invalidText: '',
    label: '',
    labelDescription: '',
    placeholder: '',
    readableName: '',
    required: false,
    resizable: true,
    rows: 3,
    showCorrectState: false,
}
